'use client';

import {debounce} from 'lodash';
import {useRef, useEffect, useMemo} from 'react';

/**
 * Sometimes callback requires to be changed frequently
 * which can reset debounce when used improperly with useCallback
 * this ensures callback is its latest version whiel also preserving debounce
 */
export const useDebounce = <T extends (...args: any[]) => any>(
	callback: T,
	delay = 750
): ((...args: Parameters<T>) => ReturnType<T>) => {
	const dispatchRef = useRef(callback);

	useEffect(() => {
		dispatchRef.current = callback;
	}, [callback]);

	return useMemo(
		//eslint-disable-next-line @typescript-eslint/no-unsafe-return
		() => debounce((...args: Parameters<T>) => dispatchRef.current(...args), delay),
		[]
	);
};

// https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
export const usePrevious = <T>(value: T): T => {
	const ref = useRef<T>(value);
	useEffect(() => {
		ref.current = value;
	});
	return ref.current;
};
